import countryList from 'react-select-country-list'

import { isPremiumPlanSlug } from '@/domain/subscription/plans'
import { AuthUser__UserFragment as User } from '@/graphql/api/generated'
import { UserRoutes } from '@/routes/_hooks/routes.enum'
import { generatePath } from '@/routing'

import { IChatService } from './interfaces'

interface ChatwootSettings {
  hideMessageBubble?: boolean
  position?: 'left' | 'right'
  locale?: string
  type?: 'standard' | 'expanded_bubble'
  darkMode?: 'light' | 'auto'
  enabledFeatures?: string[]
  launcherTitle?: string
  useBrowserLanguage?: boolean
  showUnreadIndicator?: boolean
}

interface ChatwootUserData {
  email: string
  name: string
  thumbnail?: string
  phone_number?: string
  description?: string
  country_code?: string
  country?: string
  city?: string
  company_name?: string
  avatar_url?: string
  additional_attributes?: Record<string, string>
  custom_attributes?: ChatwootCustomAttributes
  profile?: string
}

interface ChatwootCustomAttributes {
  gender?: string
  language?: string
  communities?: string
  profile?: string
}

declare global {
  interface Window {
    chatwootSDK: {
      run: (config: { websiteToken: string; baseUrl: string }) => void
    }
    $chatwoot: {
      setUser: (id: string, userData: ChatwootUserData) => void
      setLabel: (labels: string[]) => void
      setCustomAttributes: (attributes: ChatwootCustomAttributes) => void
      reset: () => void
      toggle: (state?: 'open' | 'close') => void
      setLocale: (locale: string) => void
    }
    chatwootSettings: ChatwootSettings
  }
}

export class ChatwootService implements IChatService {
  private isInitialized = false
  private config: {
    baseUrl: string
    websiteToken: string
  }
  private pendingUser: User | null = null
  private pendingToggle: { state?: 'open' | 'close' } | null = null

  constructor(baseUrl: string, websiteToken: string) {
    this.config = { baseUrl, websiteToken }
    this.initialize()
  }

  init(): void {
    this.initialize()
  }

  private initialize(): void {
    if (this.isInitialized) return

    // Configure before loading script
    // Widget is hidden by default and opened via the sidebar support button
    window.chatwootSettings = {
      hideMessageBubble: true,
      position: 'right',
      locale: 'en',
      type: 'standard',
      darkMode: 'auto',
      useBrowserLanguage: false,
      showUnreadIndicator: true,
      enabledFeatures: ['emoji_picker', 'attachments'],
    }

    // Use the standard Chatwoot loading method
    const script = document.createElement('script')
    script.src = `${this.config.baseUrl}/packs/js/sdk.js`
    script.defer = true
    script.async = true

    script.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: this.config.websiteToken,
          baseUrl: this.config.baseUrl,
        })
        this.isInitialized = true
        this.processPendingCalls()
      }
    }

    document.head.appendChild(script)
  }

  private processPendingCalls(): void {
    if (this.pendingUser) {
      this.setUser(this.pendingUser)
      this.pendingUser = null
    }

    if (this.pendingToggle) {
      this.toggle(this.pendingToggle.state)
      this.pendingToggle = null
    }
  }

  setUser(user: User): void {
    if (!user?.email) return

    if (!this.isInitialized || !window.$chatwoot) {
      this.pendingUser = user
      return
    }

    const communities = (user.circleMemberships?.edges ?? []).map(
      (edge) => edge.node!.circle
    )
    // Keyed on the plan slug, not on its name: names are translated, so lowercasing them only
    // ever matched the French labels.
    const proCommunities = communities.filter((c) =>
      isPremiumPlanSlug(
        c?.networks?.edges?.[0]?.node?.subscription?.plan?.slug
      )
    )

    const customAttributes: ChatwootCustomAttributes = {
      gender: user.gender,
      language: user.settings?.language,
      communities: communities.map((c) => c.name).join(', '),
      profile: `${window.location.origin}${generatePath(UserRoutes.Profile, { slug: user.slug })}`,
    }

    const userData: ChatwootUserData = {
      email: user.email,
      name: user.fullname,
      phone_number: user.phoneNumber?.internationalNumber,
      country:
        countryList()
          .getData()
          .find((c) => c.label === user.country)?.value ?? 'FR',
      avatar_url: user.avatar?.contentUrl,
      city: user.city,
      description: user.bio ?? '',
      company_name: proCommunities
        .map((c) => {
          return `${c.name} (${c.networks?.edges?.[0]?.node?.subscription?.plan?.name})`
        })
        .join(', '),
      custom_attributes: customAttributes,
    }

    window.$chatwoot.setUser(user._Id, userData)
  }

  reset(): void {
    this.pendingUser = null
    this.pendingToggle = null

    if (!this.isInitialized || !window.$chatwoot) return
    window.$chatwoot.reset()
  }

  toggle(state?: 'open' | 'close'): void {
    if (!this.isInitialized || !window.$chatwoot) {
      this.pendingToggle = { state }
      return
    }

    window.$chatwoot.toggle(state)
  }
}
